import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../assets/icons/LineDot";
import {
  ChartTitle,
  StyledSubtitle,
  StyledTile,
} from "../../../../../../components/ChartCard/ChartCard.styles";
import { ChartCardLoading } from "../../../../../../components/ChartCard/ChartCardLoading";
import { ChartSpecificValues } from "../../../../../../components/ChartCard/ChartSpecificValues/ChartSpecificValues";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { ChartLegendContainer } from "../../../../../../components/ModalChart/CustomModalChartLegend/CustomModalChartLegend.style";
import { useTreatments } from "../../../../../../hooks/queries/treatments";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import {
  buildMonthlyAvg,
  centerPerDay,
  getGraphSeparators,
} from "../../../../../../utils/chartUtils";
import {
  getLongFormattedDate,
  getRelativeTimeSinceDate,
} from "../../../../../../utils/dateFormatter";
import {
  DATE_FILTER_48H,
  DATE_FILTER_ALL,
} from "../../../MeasurementModal/components/CustomOptionsBar";
import { NebulizerModalChart } from "../../../MeasurementModal/components/NebulizerModalChart";
import {
  GraphContainer,
  GraphLegend,
  NebulizerChartDataContainer,
} from "./NebulizerChartCard.styles";

export type NebulizerChartCardProps = {
  patientId?: string;
  title: string;
  onClick: React.MouseEventHandler<HTMLDivElement>;
};

export function NebulizerChartCard({
  patientId,
  title,
  onClick,
}: NebulizerChartCardProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter } = useUserPreferences();
  const dateRangeFilter = getDateRangeFilter();
  const { treatments, isTreatmentsFetching, isTreatmentsRefetching } =
    useTreatments(
      parseInt(patientId ?? "0", 10),
      dateRangeFilter.id === DATE_FILTER_48H ||
        dateRangeFilter.id === DATE_FILTER_ALL
        ? dateRangeFilter.startDate
        : dateRangeFilter.startDate.startOf("day"),
      dateRangeFilter.endDate.endOf("day"),
      "nebulizer"
    );

  const mainValues = useMemo(() => {
    if (!Array.isArray(treatments) || treatments.length === 0) {
      return { min: null as number | null, max: null as number | null };
    }

    return treatments.reduce(
      (acc, t) => {
        const ts = new Date(t.time).getTime();
        if (!Number.isFinite(ts)) return acc;
        if (acc.min === null || ts < acc.min) acc.min = ts;
        if (acc.max === null || ts > acc.max) acc.max = ts;
        return acc;
      },
      { min: null as number | null, max: null as number | null }
    );
  }, [treatments]);

  const specificValues = useMemo(() => {
    const lastSyncIso = mainValues.max
      ? dayjs(mainValues.max).toISOString()
      : null;
    const lastSyncDisplay = lastSyncIso
      ? getRelativeTimeSinceDate(getLongFormattedDate(lastSyncIso))
      : "-";

    return [
      { label: t("patientPage_card_lastSynced"), value: lastSyncDisplay },
      {
        label: t("patientPage_card_treatmentCount"),
        value: Array.isArray(treatments) ? treatments.length : 0,
      },
      { label: "", value: "" },
    ];
  }, [mainValues.max, treatments?.length, t]);

  const { interval } = getGraphSeparators(
    dateRangeFilter,
    undefined,
    undefined
  );

  const treatmentData = useMemo(() => {
    if (!treatments) return [];

    const base = treatments.map((t) => ({
      ...t,
      timeValue: new Date(t.time).getTime(),
      totalTreatmentTimeNum: Number.isFinite(+t.totalTreatmentTime)
        ? +t.totalTreatmentTime
        : 0,
    }));

    if (interval === "7D" || interval === "1M" || interval === "2M") {
      return centerPerDay(base, interval);
    }

    if (interval === "6M") {
      return buildMonthlyAvg(
        treatments,
        dateRangeFilter.startDate.valueOf(),
        dateRangeFilter.endDate.valueOf(),
        4
      );
    }

    if (interval === "1Y") {
      return buildMonthlyAvg(
        treatments,
        dateRangeFilter.startDate.valueOf(),
        dateRangeFilter.endDate.valueOf(),
        2
      );
    }

    if (interval === "4Y" || interval === "4Y+") {
      return buildMonthlyAvg(
        treatments,
        dateRangeFilter.startDate.valueOf(),
        dateRangeFilter.endDate.valueOf(),
        6
      );
    }

    return base;
  }, [treatments, interval]);

  const isEmptyState = !treatments || treatments.length === 0;
  return (
    <StyledTile onClick={onClick}>
      {isTreatmentsFetching || isTreatmentsRefetching ? (
        <ChartCardLoading />
      ) : (
        <>
          <ChartTitle>{title}</ChartTitle>
          {isEmptyState ? (
            <StyledSubtitle>{t("patientPage_card_noData")}</StyledSubtitle>
          ) : null}

          <NebulizerChartDataContainer isEmpty={treatmentData.length === 0}>
            <GraphContainer>
              {treatmentData.length > 0 ? (
                <GraphLegend>
                  <ChartLegendContainer>
                    <StyledLegend key="Incomplete">
                      <LineDot fill="#F5A45E" />
                      {t("patientPage_measurementModal_nebulizer_incomplete")}
                    </StyledLegend>
                    <StyledLegend key="Complete">
                      <LineDot fill="#87D5A9" />
                      {t("patientPage_measurementModal_nebulizer_complete")}
                    </StyledLegend>
                  </ChartLegendContainer>
                </GraphLegend>
              ) : null}
              <NebulizerModalChart
                data={treatmentData}
                dateInterval={dateRangeFilter}
                height="90%"
                width="100%"
                onlyGraph
                minDate={mainValues.min ? dayjs(mainValues.min) : undefined}
                maxDate={mainValues.max ? dayjs(mainValues.max) : undefined}
              />
            </GraphContainer>

            <ChartSpecificValues
              values={specificValues}
              isEmpty={treatmentData.length === 0}
            />
          </NebulizerChartDataContainer>
        </>
      )}
    </StyledTile>
  );
}
